import type { RefinementCtx } from 'zod';
import { z } from 'zod';
import { isHttpProtocol } from './utils/http.js';

export const CACHE_MODES = ['default', 'bypass'] as const;
export type CacheMode = (typeof CACHE_MODES)[number];

export const SummaryLengthSchema = z.enum(['short', 'medium', 'long', 'xl', 'xxl']);
export type SummaryLength = z.infer<typeof SummaryLengthSchema>;

export const SupportedModels = z.enum(['model.reasoning.fast', 'model.reasoning.balanced', 'model.reasoning.premium']);

const LinkPreviewUrlSchema = z
  .string()
  .url('Invalid URL format for summarization')
  .superRefine((value, ctx: RefinementCtx) => {
    try {
      const parsed = new URL(value);
      if (!isHttpProtocol(parsed.protocol)) {
        ctx.addIssue({
          code: 'custom',
          message: 'Only HTTP and HTTPS URLs can be summarized',
        });
      }
    } catch {
      ctx.addIssue({ code: 'custom', message: 'Invalid URL format for summarization' });
    }
  });

const LinkPreviewBaseSchema = z.object({
  url: LinkPreviewUrlSchema,
});

export const CacheModeSchema = z.enum(CACHE_MODES);

const ShareContextEntrySchema = z.object({
  author: z.string().trim().min(1).max(80),
  handle: z.string().trim().min(1).max(80).optional(),
  text: z.string().trim().min(1).max(400),
  likeCount: z.number().int().min(0).optional(),
  reshareCount: z.number().int().min(0).optional(),
  replyCount: z.number().int().min(0).optional(),
  timestamp: z.string().trim().min(1).max(40).optional(),
});

export const LinkPreviewSummaryRequestSchema = LinkPreviewBaseSchema.extend({
  summaryLength: SummaryLengthSchema.optional(),
  model: SupportedModels.optional(),
  cacheMode: CacheModeSchema.optional(),
  shareContext: z.array(ShareContextEntrySchema).max(8).optional(),
});

export type LinkPreviewSummaryRequest = z.infer<typeof LinkPreviewSummaryRequestSchema>;

export const LinkPreviewContextRequestSchema = LinkPreviewBaseSchema;
export type LinkPreviewContextRequest = z.infer<typeof LinkPreviewContextRequestSchema>;

export const LinkPreviewContextResponseSchema = z.object({
  url: z.string(),
  title: z.string().nullable(),
  description: z.string().nullable(),
  siteName: z.string().nullable(),
  content: z.string(),
  truncated: z.boolean(),
  totalCharacters: z.number(),
  wordCount: z.number(),
  transcriptCharacters: z.number().nullable(),
  transcriptLines: z.number().nullable(),
  characterBudget: z.number(),
});

export type LinkPreviewContextResponse = z.infer<typeof LinkPreviewContextResponseSchema>;
